/**
 * ============================================================================
 * GLOSSARY SELECTORS — map the metric catalog to the glossary tab + tooltips.
 * ============================================================================
 *
 * LAYER: pure selector functions (portal convention) over `getMetricDefinitions`
 * — the SAME catalog the tables read. The glossary is served ON the catalog
 * (spec hilt-wbr-api-serving): each definition carries `description` (the
 * authoritative definition text), `reportingBasis`, and `example`; the response
 * carries `glossaryTerms` (non-metric entries). The tooltip lookup and the
 * glossary page therefore share one source and can never disagree, and nothing
 * is hardcoded per metric.
 *
 * JOIN KEY (tooltips): the metric display NAME, normalized — a rename that
 * reaches the catalog moves both the tooltip and the glossary row together, so
 * the cell shows the current definition or no icon, never a stale one.
 *
 * Spec: .kiro/specs/metric-definition-tooltips, .kiro/specs/glossary-enhancements
 */

import { GetMetricDefinitionsOutput } from 'src/types/metricsApi.types';
import { GlossaryResponse } from 'src/types';

/** trim, lowercase, collapse internal whitespace — tolerant of formatting drift. */
export function normalizeMetricName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * The metric catalog reshaped into the glossary tab's two tables: metric
 * definitions (from `definitions`, ordered by sortOrder) and non-metric terms
 * (from `glossaryTerms`). `reportingBasis`/`example` are optional on the
 * catalog, so absent values render blank. Empty when the catalog has not
 * resolved.
 */
export function selectGlossary(
  catalog: GetMetricDefinitionsOutput | undefined,
): GlossaryResponse {
  const metricDefinitions = (catalog?.definitions ?? [])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((d) => ({
      order: d.sortOrder,
      metricName: d.name,
      reportingBasis: d.reportingBasis ?? '',
      definition: d.description,
      example: d.example ?? '',
    }));
  const terms = (catalog?.glossaryTerms ?? []).map((t) => ({
    category: t.category,
    term: t.term,
    description: t.description,
  }));
  return { metricDefinitions, terms };
}

/**
 * Normalized metricName → definition text, for the metric-definition tooltips.
 * Built straight from the catalog's `description`. Empty map when the catalog
 * has not resolved — callers render unchanged until it does (R1.5).
 */
export function buildDefinitionLookup(
  catalog: GetMetricDefinitionsOutput | undefined,
): Map<string, string> {
  const lookup = new Map<string, string>();
  for (const d of catalog?.definitions ?? []) {
    const name = normalizeMetricName(d.name);
    const definition = (d.description ?? '').trim();
    if (name && definition) lookup.set(name, definition);
  }
  return lookup;
}
